// Mailgun API client setup and configuration
// This module handles Mailgun API authentication and base client setup

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { mailgunConfig } from '../../../config/api.config';

/**
 * Mailgun API configuration interface
 */
export interface MailgunConfig {
  domain: string;
  apiKey: string;
  baseUrl: string;
}

/**
 * Request parameters for Mailgun events API
 */
export interface EventsRequestParams {
  begin?: string;
  end?: string;
  ascending?: 'yes' | 'no';
  limit?: number;
  pretty?: 'yes' | 'no';
  recipient?: string;
  from?: string;
  to?: string;
  subject?: string;
  event?: string;
  tags?: string;
  severity?: 'temporary' | 'permanent';
  'message-id'?: string; // Add support for message-id parameter
}

/**
 * Request parameters for Mailgun stats API
 */
export interface StatsRequestParams {
  event: string[];
  start: string;
  end?: string;
  resolution?: 'hour' | 'day' | 'month';
  duration?: string;
}

/**
 * Validate Mailgun configuration
 */
export const validateMailgunConfig = (config: typeof mailgunConfig): boolean => {
  if (!config.apiKey || config.apiKey === 'your-mailgun-api-key') {
    console.error('Mailgun API key is not configured');
    return false;
  }

  if (!config.domain || config.domain === 'mg.distro-nation.com') {
    console.warn('Using default Mailgun domain, ensure this is correct');
  }

  // Validate API key format (should start with 'key-')
  if (!config.apiKey.startsWith('key-')) {
    console.warn('Mailgun API key should start with "key-"');
  }

  return true;
};

/**
 * Get Mailgun API configuration with validation
 */
export const getMailgunConfig = (): MailgunConfig => {
  const isValid = validateMailgunConfig(mailgunConfig);
  
  if (!isValid) {
    throw new Error('Invalid Mailgun configuration. Please check your environment variables.');
  }

  return {
    domain: mailgunConfig.domain,
    apiKey: mailgunConfig.apiKey,
    baseUrl: `https://api.mailgun.net/v3/${mailgunConfig.domain}`,
  };
};

/**
 * Create Mailgun API client with authentication and enhanced configuration
 */
export const createMailgunClient = (): AxiosInstance => {
  const config = getMailgunConfig();
  
  const client = axios.create({
    baseURL: config.baseUrl,
    auth: {
      username: 'api',
      password: config.apiKey,
    },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 30000, // 30 second timeout
  });

  // Add request interceptor for logging
  client.interceptors.request.use(
    (config) => {
      console.log(`Making Mailgun API request: ${config.method?.toUpperCase()} ${config.url}`);
      return config;
    },
    (error) => {
      console.error('Mailgun API request error:', error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor for error handling
  client.interceptors.response.use(
    (response) => {
      console.log(`Mailgun API response: ${response.status} ${response.statusText}`);
      return response;
    },
    (error) => {
      console.error('Mailgun API response error:', error.response?.data || error.message);
      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * Format query parameters for Mailgun events API
 */
export const formatEventsParams = (params: EventsRequestParams): URLSearchParams => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, v.toString()));
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });

  return searchParams;
};

/**
 * Format query parameters for Mailgun stats API
 */
export const formatStatsParams = (params: StatsRequestParams): URLSearchParams => {
  const searchParams = new URLSearchParams();
  
  // Handle event array
  if (params.event && params.event.length > 0) {
    params.event.forEach(event => searchParams.append('event', event));
  }

  // Handle other parameters
  Object.entries(params).forEach(([key, value]) => {
    if (key !== 'event' && value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  return searchParams;
};

/**
 * Create a GET request configuration for Mailgun API
 */
export const createGetRequest = (endpoint: string, params?: Record<string, any>): AxiosRequestConfig => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: endpoint,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  if (params) {
    config.params = params;
  }

  return config;
};

/**
 * Create a POST request configuration for Mailgun API
 */
export const createPostRequest = (endpoint: string, data: FormData | Record<string, any>): AxiosRequestConfig => {
  return {
    method: 'POST',
    url: endpoint,
    data,
    headers: {
      'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/x-www-form-urlencoded',
    },
  };
};

/**
 * Handle Mailgun API rate limiting with exponential backoff
 * @deprecated Use the new MailgunRateLimiter class from rateLimiting.ts instead
 */
export const handleRateLimit = async (retryCount: number = 0): Promise<void> => {
  const maxRetries = 3;
  const baseDelay = 1000; // 1 second

  if (retryCount >= maxRetries) {
    throw new Error('Maximum retry attempts reached for Mailgun API rate limit');
  }

  const delay = baseDelay * Math.pow(2, retryCount);
  console.log(`Rate limited. Retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
  
  await new Promise(resolve => setTimeout(resolve, delay));
};

/**
 * Create a rate-limited request function using the enhanced rate limiter
 */
export const createRateLimitedRequest = async <T>(
  client: AxiosInstance,
  config: AxiosRequestConfig,
  requestId?: string
): Promise<T> => {
  const { rateLimitedRequest } = await import('./rateLimiting');
  
  return rateLimitedRequest(
    () => client.request<T>(config).then(response => response.data),
    requestId,
    { enableLogging: process.env.NODE_ENV === 'development' }
  );
};